import type { FilterNode } from '@xphoto/shared';
import type { MessageKey, TFunction } from '../i18n';
import { interpolate } from './format';

/**
 * ÉCHO EN LANGAGE NATUREL d'une expression de filtre (AST → phrase localisée). Relit un
 * `FilterNode` (mini-langage `filter-expr.ts` de `@xphoto/shared`) en une lecture claire
 * dans la langue de l'UI : chaque FEUILLE devient une courte phrase (« taguées « plage » »,
 * « notées 4★ et plus », « de type vidéo »…), reliées par des mots (« et » / « ou »), la
 * négation rendue par « sauf … », et les groupes parenthésés au strict nécessaire (même
 * priorité que le sérialiseur canonique).
 *
 * Fonction PURE : aucune dépendance UI/DOM, la seule entrée localisée est la `t` passée en
 * paramètre. Le mini-langage lui-même (`tag:`, `ET`/`OU`…) reste en français canonique
 * dans le CHAMP de saisie — ceci n'en est QUE la relecture pour l'utilisateur.
 */

/** Priorités (miroir de `filterAstToText`) — pour parenthéser au strict nécessaire. */
const OR_PREC = 1;
const AND_PREC = 2;
const NOT_PREC = 3;
const ATOM_PREC = 4;

type TypeValue = Extract<FilterNode, { kind: 'type' }>['value'];
type StatusValue = Extract<FilterNode, { kind: 'status' }>['value'];
type RatingOp = Extract<FilterNode, { kind: 'rating' }>['op'];

/** Nom localisé de chaque valeur de `type` (clés littérales → détectées par la garde i18n). */
const TYPE_NAME_KEY: Record<TypeValue, MessageKey> = {
  image: 'filterExpr.typeName.image',
  animatedImage: 'filterExpr.typeName.animatedImage',
  video: 'filterExpr.typeName.video',
  videoWithSound: 'filterExpr.typeName.videoWithSound',
};

/** Nom localisé de chaque valeur de `statut`. */
const STATUS_NAME_KEY: Record<StatusValue, MessageKey> = {
  tagged: 'filterExpr.statusName.tagged',
  untagged: 'filterExpr.statusName.untagged',
  aiPending: 'filterExpr.statusName.aiPending',
};

/** Gabarit localisé de chaque comparaison de note. */
const RATING_KEY: Record<RatingOp, MessageKey> = {
  gte: 'filterExpr.leaf.ratingGte',
  lte: 'filterExpr.leaf.ratingLte',
  eq: 'filterExpr.leaf.ratingEq',
};

const precedence = (node: FilterNode): number => {
  switch (node.kind) {
    case 'or':
      return OR_PREC;
    case 'and':
      return AND_PREC;
    case 'not':
      return NOT_PREC;
    default:
      return ATOM_PREC;
  }
};

/** Rend un enfant, en le parenthésant si sa priorité est plus faible que le contexte. */
const wrapChild = (node: FilterNode, minPrec: number, t: TFunction): string => {
  const s = render(node, t);
  return precedence(node) < minPrec ? `(${s})` : s;
};

const render = (node: FilterNode, t: TFunction): string => {
  switch (node.kind) {
    case 'or':
      return node.children.map((c) => wrapChild(c, AND_PREC, t)).join(` ${t('filterExpr.or')} `);
    case 'and':
      return node.children.map((c) => wrapChild(c, NOT_PREC, t)).join(` ${t('filterExpr.and')} `);
    case 'not':
      return interpolate(t('filterExpr.except'), { x: wrapChild(node.child, ATOM_PREC, t) });
    case 'tag':
      return interpolate(t('filterExpr.leaf.tag'), { label: node.value });
    case 'type':
      return interpolate(t('filterExpr.leaf.type'), { type: t(TYPE_NAME_KEY[node.value]) });
    case 'rating':
      return interpolate(t(RATING_KEY[node.op]), { value: node.value });
    case 'folder':
      return node.value === ''
        ? t('filterExpr.leaf.folderRoot')
        : interpolate(t('filterExpr.leaf.folder'), { folder: node.value });
    case 'filename':
      return interpolate(t('filterExpr.leaf.filename'), { name: node.value });
    case 'status':
      return interpolate(t('filterExpr.leaf.status'), { status: t(STATUS_NAME_KEY[node.value]) });
  }
};

/** Relit un AST d'expression de filtre en langage clair localisé (voir en-tête du module). */
export function filterExprToHuman(ast: FilterNode, t: TFunction): string {
  return render(ast, t);
}
